<template>
  <section class="form-section">
    <h1 class="form-section__title">
      {{ $t('APPS.INFO.CHANGE_PRICE') }}
      <a
        v-show="!isEditing"
        v-if="isProductsDigital"
        class="form-section__action-btn"
        role="button"
        @click.prevent="isEditing = true"
      >
        <i class="el-icon-edit form-section__action-btn-icon" />
        <span class="form-section__edit-title">
          {{ $t('EDIT') }}
        </span>
      </a>
    </h1>

    <div class="row align-justify">
      <div class="small-12 large-4 columns">
        <p
          v-dompurify-html="$t('APPS.INFO.CHANGE_PRICE_DESC')"
          class="form-section__description"
        />
      </div>

      <div class="small-12 large-7 columns">
        <ElForm
          ref="price"
          class="card"
          label-position="top"
          @submit.native.prevent="updatePrice"
        >
          <div v-show="isEditing">
            <div class="card__block">
              <ElFormItem :label="$t('APPS.LIST.PRICE')">
                <ElSelect v-model="appPriceModel" class="show-for-medium">
                  <ElOption :label="$t('FREE')" :value="0" />
                  <ElOption
                    v-for="priceAmount in priceList"
                    :key="priceAmount"
                    :label="label(priceAmount, $t('RIALS'), $t('FREE'))"
                    :value="priceAmount"
                  />
                </ElSelect>
                <select v-model="appPriceModel" class="hide-for-medium">
                  <option :value="0">
                    {{ $t('FREE') }}
                  </option>
                  <option
                    v-for="priceAmount in priceList"
                    :key="priceAmount"
                    :value="priceAmount"
                  >
                    {{ label(priceAmount, $t('RIALS'), $t('FREE')) }}
                  </option>
                </select>
              </ElFormItem>
              <p v-show="appPriceModel">
                <span class="text-secondary">
                  {{ $t('APPS.INFO.DEVS_SHARE') }}:
                </span>
                {{ format(devShare) }} {{ $t('RIALS') }}
                <br />
                <span class="text-secondary">
                  {{ $t('APPS.INFO.BAZAAR_SHARE') }}:
                </span>
                {{ format(bazaarShare) }} {{ $t('RIALS') }}
                <br />
                <span class="text-secondary">
                  {{ $t('APPS.INFO.VAT_VALUE') }}:
                </span>
                {{ format(vatValue) }} {{ $t('RIALS') }}
              </p>

              <ErrorsList v-if="priceFormErrors" :errors="priceFormErrors" />

              <ElButton type="primary" native-type="submit" size="large">
                {{ $t('SAVE') }}
              </ElButton>

              <ElButton type="text" @click.native="isEditing = false">
                {{ $t('CANCEL') }}
              </ElButton>
            </div>
          </div>

          <div v-show="!isEditing" class="card__block">
            <dl class="list list--definition list--clean">
              <dt>
                {{ $t('APPS.LIST.PRICE') }}
                <span
                  v-if="isPricePending"
                  class="chip chip--warning chip--aligned-middle chip--mini"
                >
                  {{ $t('APPS.LIST.PRICE.NEW') }}
                </span>
              </dt>
              <dd>{{ label(price, $t('RIALS'), $t('FREE')) }}</dd>
            </dl>
          </div>
        </ElForm>
      </div>
    </div>
  </section>
</template>

<script>
import { Apps } from '@/api/api';
import { APP_PRICE, APP_PRICE_PENDING_STATUS } from '@/store/types';

import priceShare from '@/mixins/price-share';

import ErrorsList from '@@/common/ErrorsList';

import { format, label } from '@/utils/number';

import packageNameMixin from '@/mixins/packageName';

export default {
  name: 'AppPrice',

  components: {
    ErrorsList,
  },

  mixins: [priceShare, packageNameMixin],

  data() {
    return {
      isEditing: false,
      priceRange: {
        from: 10000,
        to: 2000000,
        step: 10000,
      },
      appPriceModel: 0,
      priceFormErrors: null,
    };
  },

  computed: {
    price() {
      const appPrice = this.$store.getters[APP_PRICE];
      if (typeof appPrice === 'number') {
        this.appPriceModel = appPrice; // eslint-disable-line
      }
      return appPrice;
    },

    isPricePending() {
      return this.$store.getters[APP_PRICE_PENDING_STATUS];
    },

    priceList() {
      const { from = 0, to = 0, step = 1 } = this.priceRange;
      const count = Math.floor((to - from) / step);

      if (!count) {
        return [];
      }

      return Array(count + 1)
        .fill(from)
        .map((price, index) => price + step * index);
    },

    isProductsDigital() {
      const { productsType: type } = this.$auth.user.publisher;

      return type === 'digital';
    },
  },

  watch: {
    appPriceModel(newVal, oldVal) {
      if (newVal === oldVal) {
        return false;
      }
      // priceModel model is proxied via the priceShare mixin
      this.priceModel = newVal;
    },
  },

  methods: {
    format,
    label,

    updatePrice() {
      return Apps.updatePrice(
        { packageName: this.packageName },
        { price: this.appPriceModel },
      )
        .then(({ body: { price, message, is_pending: isPricePending } }) => {
          this.$message.success(message);
          this.$store.commit(APP_PRICE, price);
          this.$store.commit(APP_PRICE_PENDING_STATUS, isPricePending);
          this.isEditing = false;
          this.priceFormErrors = null;
        })
        .catch(({ body: { message, errors } = {} }) => {
          if (message) {
            this.$message.error(message);
          }

          if (errors) {
            this.priceFormErrors = [...errors];
          }
        });
    },
  },
};
</script>
